import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, Tooltip } from '@mui/material';

import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import AppButton from '@/shared/ui/AppButton';
import ScrollArea from '@/shared/ui/ScrollArea';

import {
  FilterActions,
  FilterFieldsScrollArea,
  FilterPanelRoot,
  PredefinedFilterChip,
  PredefinedFiltersContainer,
  PredefinedFiltersLabel,
  PredefinedFiltersSection,
  filterPopoverPaperSx,
} from './GeneratikTableFilterPanel.styles';
import {
  FilterFieldConfig,
  FilterValues,
  GeneratikTableFilterPanelProps,
  PredefinedFilter,
} from './GeneratikTableFilterPanel.types';
import {
  AutocompleteFilterField,
  CheckboxListFilterField,
  CurrencyRangeFilterField,
  DateRangeFilterField,
  NumberRangeFilterField,
  SelectFilterField,
  SliderFilterField,
  TextFilterField,
} from './filterFields';

const FOCUS_SCROLL_DELAY_MS = 300;
const FOCUS_INPUT_DELAY_MS = 100;
const FOCUS_RESIZE_DELAY_MS = 50;

const useFocusFieldOnOpen = (open: boolean, initialFocusFieldId?: string | null) => {
  useEffect(() => {
    if (!open || !initialFocusFieldId) return;

    const scrollTimer = setTimeout(() => {
      const fieldElement = document.querySelector(
        `[data-testid="filter-field-${initialFocusFieldId}"]`,
      );
      if (!fieldElement) return;

      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      setTimeout(() => {
        const input = fieldElement.querySelector('input, textarea') as HTMLElement | null;
        if (input) input.focus();
        else (fieldElement as HTMLElement).focus();
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, FOCUS_RESIZE_DELAY_MS);
      }, FOCUS_INPUT_DELAY_MS);
    }, FOCUS_SCROLL_DELAY_MS);

    return () => clearTimeout(scrollTimer);
  }, [open, initialFocusFieldId]);
};

const buildEmptyValues = (fields: FilterFieldConfig[]): FilterValues => {
  const result: FilterValues = {};
  for (const field of fields) {
    if (field.type === 'date-range') {
      result[field.id] = { start: '', end: '' };
    } else if (field.type === 'number-range' || field.type === 'currency-range') {
      result[field.id] = { min: '', max: '' };
    } else if (field.type === 'slider') {
      result[field.id] = { min: field.minValue, max: field.maxValue };
    } else if (
      field.type === 'checkbox-list' ||
      (field.type === 'autocomplete' && field.multiple)
    ) {
      result[field.id] = [];
    } else {
      result[field.id] = '';
    }
  }
  return result;
};

const validateRanges = (fields: FilterFieldConfig[], values: FilterValues): string | null => {
  for (const field of fields) {
    if (field.type !== 'number-range' && field.type !== 'currency-range') continue;
    const rangeValue = values[field.id] as { min?: string; max?: string } | undefined;
    const min = parseInt(rangeValue?.min || '', 10);
    const max = parseInt(rangeValue?.max || '', 10);
    if (!isNaN(min) && !isNaN(max) && min > max) {
      return 'Min value must be less than or equal to max value';
    }
  }
  return null;
};

interface FilterFieldWrapperProps {
  tooltipTitle: string | undefined;
  children: ReactNode;
}

const FilterFieldWrapper = ({ tooltipTitle, children }: FilterFieldWrapperProps) => {
  // No tooltip → no Tooltip wrapper at all. MUI's Tooltip clones its child and
  // injects aria-label/ref/event listeners into it; a Fragment child cannot
  // host any of those, so React 19 logs "Invalid prop `aria-label` supplied to
  // `React.Fragment`" on every update reconcile — one per field per panel
  // re-render — and the injected props were silently discarded anyway (the
  // disabled-field tooltip never showed for un-spanned fields).
  if (tooltipTitle === undefined) return <>{children}</>;
  // Disabled fields get a real host element. The span is also what makes the
  // tooltip fire over disabled inner controls, which emit no pointer events
  // themselves (the standard MUI disabled-element wrapper pattern).
  return (
    <Tooltip title={tooltipTitle}>
      <span>{children}</span>
    </Tooltip>
  );
};

const GeneratikTableFilterPanel = ({
  anchorEl,
  open,
  onClose,
  fields,
  values,
  onChange,
  onApply,
  onReset,
  predefinedFilters,
  campaignColumnVisibility,
  initialFocusFieldId,
}: GeneratikTableFilterPanelProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [draftValues, setDraftValues] = useState<FilterValues>(values);

  useEffect(() => {
    if (open) setDraftValues(values);
  }, [open, values]);

  useFocusFieldOnOpen(open, initialFocusFieldId);

  const handleFieldChange = useCallback((fieldId: string, value: FilterValues[string]) => {
    setDraftValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleReset = useCallback(() => {
    const empty = buildEmptyValues(fields);
    setDraftValues(empty);
    onChange?.(empty);
    onReset?.();
    onClose();
  }, [fields, onChange, onReset, onClose]);

  const handleApply = useCallback(() => {
    const error = validateRanges(fields, draftValues);
    if (error) {
      enqueueSnackbar(t(error), { variant: 'warning' });
      return;
    }
    onChange?.(draftValues);
    onApply?.(draftValues);
    onClose();
  }, [draftValues, fields, onChange, onApply, onClose, enqueueSnackbar, t]);

  const handlePredefinedFilterClick = useCallback(
    (filter: PredefinedFilter) => {
      const merged = { ...values, ...filter.values };
      setDraftValues(merged);
      onChange?.(merged);
      onApply?.(merged);
      onClose();
    },
    [values, onChange, onApply, onClose],
  );

  const isPredefinedFilterSelected = useCallback(
    (filter: PredefinedFilter) =>
      Object.entries(filter.values).every(([key, filterValue]) => {
        const currentValue = draftValues[key];
        if (filterValue === undefined) return true;
        return JSON.stringify(currentValue) === JSON.stringify(filterValue);
      }),
    [draftValues],
  );

  const renderField = (field: FilterFieldConfig) => {
    const value = draftValues[field.id];
    const disabled = campaignColumnVisibility?.[field.id] === false;
    const tooltipTitle = disabled ? (t('Campaign period is required') as string) : undefined;
    const autoOpen = initialFocusFieldId === field.id;
    const onValueChange = (next: FilterValues[string]) => handleFieldChange(field.id, next);
    const common = { field, t, disabled, autoOpen };

    switch (field.type) {
      case 'text':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <TextFilterField {...common} value={(value as string) || ''} onChange={onValueChange} />
          </FilterFieldWrapper>
        );
      case 'select':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <SelectFilterField
              {...common}
              value={(value as string) || ''}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'date-range':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <DateRangeFilterField
              {...common}
              value={(value as { start?: string; end?: string }) || {}}
              onChange={onValueChange}
              disablePast={field.disablePast}
              shouldDisableDate={field.shouldDisableDate}
              hideClear={field.removable === false}
            />
          </FilterFieldWrapper>
        );
      case 'number-range':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <NumberRangeFilterField
              {...common}
              value={(value as { min?: string; max?: string }) || {}}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'currency-range':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <CurrencyRangeFilterField
              {...common}
              value={(value as { min?: string; max?: string }) || {}}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'checkbox-list':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <CheckboxListFilterField
              {...common}
              value={(value as string[]) || []}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'autocomplete':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <AutocompleteFilterField
              {...common}
              value={(value as string | string[]) || (field.multiple ? [] : '')}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'radio-list':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <CheckboxListFilterField
              field={{ ...field, multiple: false }}
              t={t}
              disabled={disabled}
              autoOpen={autoOpen}
              value={(value as string) || ''}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      case 'slider':
        return (
          <FilterFieldWrapper key={field.id} tooltipTitle={tooltipTitle}>
            <SliderFilterField
              field={field}
              t={t}
              disabled={disabled}
              value={(value as { min?: number; max?: number }) || {}}
              onChange={onValueChange}
            />
          </FilterFieldWrapper>
        );
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      data-testid="filter-panel"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: filterPopoverPaperSx } }}
    >
      <FilterPanelRoot>
        <FilterFieldsScrollArea>
          <ScrollArea maxHeight="calc(70vh - 4rem)">
            {predefinedFilters && predefinedFilters.length > 0 && (
              <PredefinedFiltersSection>
                <PredefinedFiltersLabel>{t('Quick filters')}</PredefinedFiltersLabel>
                <PredefinedFiltersContainer>
                  {predefinedFilters.map((filter) => (
                    <PredefinedFilterChip
                      key={filter.id}
                      selected={isPredefinedFilterSelected(filter)}
                      onClick={() => handlePredefinedFilterClick(filter)}
                      title={filter.tooltip}
                      data-testid={`predefined-filter-${filter.id}`}
                    >
                      {filter.label}
                    </PredefinedFilterChip>
                  ))}
                </PredefinedFiltersContainer>
              </PredefinedFiltersSection>
            )}

            {fields.map(renderField)}
          </ScrollArea>
        </FilterFieldsScrollArea>

        <FilterActions>
          <AppButton
            variant="ghost"
            size="medium"
            onClick={handleReset}
            data-testid="filter-reset-button"
          >
            {t('buttons.reset')}
          </AppButton>
          <AppButton
            variant="primary"
            size="medium"
            onClick={handleApply}
            data-testid="apply-filters-button"
          >
            {t('Apply')}
          </AppButton>
        </FilterActions>
      </FilterPanelRoot>
    </Popover>
  );
};

export default GeneratikTableFilterPanel;
